import { useEffect, useRef, useState } from "react";
import { Button, Input, message } from "antd";
import { useTranslation } from "react-i18next";
import {
  CODE_LENGTH,
  TWO_FACTOR_METHOD,
  sanitizeCode,
} from "../profile/constants";
import { describePasskeyError, getPasskeyAssertion } from "utils/webauthn";
import {
  resendTwoFactorChallenge,
  verifyPasskeyChallenge,
  verifyTwoFactorChallenge,
} from "../../api";

/** The link that offers each of the other enrolled methods, by the method it switches to. */
const SWITCH_LABEL = {
  [TWO_FACTOR_METHOD.SMS]: "profile.login.useSms",
  [TWO_FACTOR_METHOD.TOTP]: "profile.login.useTotp",
  [TWO_FACTOR_METHOD.PASSKEY]: "profile.login.usePasskey",
};

/**
 * The second step of signing in, shown in place of the credentials form once the API
 * answers `two_factor_required`.
 *
 * It is a step, not a dialog: the password has been accepted and there is nothing behind it
 * to go back to except starting over, which is what the link at the bottom does.
 *
 * The challenge handle is the only thing tying this screen to an account — no user id is
 * held here, and none is sent. A rejected code is shown against the input rather than as a
 * global toast, because on a sign-in screen a floating red banner reads as a crash rather
 * than as "try again".
 *
 * A passkey answers the same challenge without a code: the button hands the server's options
 * to the authenticator and posts back what it signed. It is a button rather than something
 * that fires on mount because Safari will only open the prompt from a user gesture, and a
 * dialog that appears unasked reads as a hijack even where it is allowed.
 */
const TwoFactorChallenge = ({
  challenge,
  slug,
  isDark,
  onVerified,
  onCancel,
}) => {
  const { t } = useTranslation();
  const [code, setCode] = useState("");
  const [method, setMethod] = useState(challenge?.method);
  const [destination, setDestination] = useState(challenge?.destination);
  const [ceremony, setCeremony] = useState({
    options: challenge?.options,
    token: challenge?.ceremony_token,
  });
  const [error, setError] = useState(null);
  const [verifying, setVerifying] = useState(false);
  const [resending, setResending] = useState(false);
  const inputRef = useRef(null);

  const isSms = method === TWO_FACTOR_METHOD.SMS;
  const isPasskey = method === TWO_FACTOR_METHOD.PASSKEY;

  useEffect(() => {
    // Nothing to focus on the passkey step — it has a button, not an input.
    if (!isPasskey) inputRef.current?.focus();
  }, [isPasskey]);

  /** The other enrolled methods, each offered as its own way in. */
  const alternatives = (challenge?.available_methods ?? []).filter(
    (candidate) => candidate !== method
  );

  const verify = async () => {
    if (code.length !== CODE_LENGTH || verifying) return;

    setVerifying(true);
    const response = await verifyTwoFactorChallenge({
      slug,
      challenge_token: challenge.challenge_token,
      code,
    });
    setVerifying(false);

    if (response?.status) {
      onVerified(response.data);
      return;
    }

    setCode("");
    setError(response?.message || t("login.login_err"));
    inputRef.current?.focus();
  };

  /**
   * Answers the same challenge with a signature instead of a code.
   *
   * A rejected assertion leaves the ceremony handle alive, so pressing the button again
   * simply re-runs the prompt — the sign-in challenge's own failure counter is what
   * eventually ends the attempt, exactly as it does for a mistyped code.
   */
  const verifyWithPasskey = async () => {
    if (verifying || !ceremony.options) return;

    setVerifying(true);
    setError(null);

    let credential;

    try {
      credential = await getPasskeyAssertion(ceremony.options);
    } catch (passkeyError) {
      setVerifying(false);
      // Null means the user dismissed the prompt; there is nothing to tell them.
      setError(describePasskeyError(passkeyError, t));
      return;
    }

    const response = await verifyPasskeyChallenge({
      slug,
      challenge_token: challenge.challenge_token,
      ceremony_token: ceremony.token,
      credential,
    });
    setVerifying(false);

    if (response?.status) {
      onVerified(response.data);
      return;
    }

    setError(response?.message || t("login.login_err"));
  };

  /**
   * Re-sends the code, optionally switching method. Switching to the authenticator sends
   * nothing — there is nothing to send — so the only visible change is the prompt; switching
   * to a passkey returns fresh options to sign rather than anything to type.
   */
  const resend = async (nextMethod) => {
    setResending(true);
    const response = await resendTwoFactorChallenge({
      slug,
      challenge_token: challenge.challenge_token,
      ...(nextMethod ? { method: nextMethod } : {}),
    });
    setResending(false);

    if (!response?.status) {
      setError(response?.message || t("formValidationMessages.serverError"));
      return;
    }

    const switchedTo = response.data?.method ?? nextMethod ?? method;

    setMethod(switchedTo);
    setDestination(response.data?.destination ?? null);
    setCeremony({
      options: response.data?.options,
      token: response.data?.ceremony_token,
    });
    setCode("");
    setError(null);

    if (switchedTo !== TWO_FACTOR_METHOD.PASSKEY) inputRef.current?.focus();

    if (response.data?.method === TWO_FACTOR_METHOD.SMS) {
      message.success(t("profile.login.codeSent"));
    }
  };

  return (
    <div className="mt-8">
      <h2
        className={`text-xl font-bold mb-2.5 ${
          isDark ? "login-text-color" : "text-brand"
        }`}
      >
        {t("profile.login.twoFactorTitle")}
      </h2>

      <p
        className={`text-sm mb-6 ${
          isDark ? "text-text-subtle" : "text-text-muted"
        }`}
      >
        {isPasskey && t("profile.login.passkeyPrompt")}

        {isSms && (
          <>
            {t("profile.login.smsSentTo")}{" "}
            <span
              className="font-bold"
              style={{ direction: "ltr", unicodeBidi: "bidi-override" }}
            >
              {destination}
            </span>
          </>
        )}

        {!isPasskey && !isSms && t("profile.login.totpPrompt")}
      </p>

      {!isPasskey && (
        <Input
          ref={inputRef}
          value={code}
          onChange={(event) => {
            setCode(sanitizeCode(event.target.value));
            setError(null);
          }}
          onPressEnter={verify}
          maxLength={CODE_LENGTH}
          inputMode="numeric"
          autoComplete="one-time-code"
          aria-label={t("profile.twoFactor.code")}
          placeholder="______"
          status={error ? "error" : undefined}
          className="!h-[56px] text-center text-[22px] tracking-[10px] font-mono"
          style={{ direction: "ltr" }}
        />
      )}

      {error && <div className="text-danger text-xs mt-2">{error}</div>}

      <Button
        className="bg-brand hover:bg-brand-hover rounded-lg flex items-center justify-center w-full h-[48px] sm:h-[56px] text-surface font-semibold mt-6 text-sm sm:text-base"
        type="primary"
        loading={verifying}
        disabled={isPasskey ? !ceremony.options : code.length !== CODE_LENGTH}
        onClick={isPasskey ? verifyWithPasskey : verify}
      >
        {isPasskey
          ? t("profile.login.passkeyVerify")
          : t("profile.login.verify")}
      </Button>

      <div className="flex flex-col items-center gap-2 mt-5 text-xs">
        {isSms && (
          <button
            type="button"
            onClick={() => resend()}
            disabled={resending}
            className="text-brand hover:text-brand-hover disabled:opacity-50"
          >
            {t("profile.login.resend")}
          </button>
        )}

        {alternatives.map((alternative) => (
          <button
            key={alternative}
            type="button"
            onClick={() => resend(alternative)}
            disabled={resending}
            className="text-brand hover:text-brand-hover disabled:opacity-50"
          >
            {t(SWITCH_LABEL[alternative])}
          </button>
        ))}

        <button
          type="button"
          onClick={onCancel}
          className={isDark ? "text-text-subtle" : "text-text-muted"}
        >
          {t("profile.login.back")}
        </button>
      </div>
    </div>
  );
};

export default TwoFactorChallenge;
